import { useTypedSelector } from '@Features/store';
import PermissionContainer from '@Pages/Assistants/Components/AssistantPermissions/PermissionContainer';
import { grantsGetAll } from '@Pages/Assistants/Slices/Grants/Grants.slices';
import { Grants } from '@Pages/Assistants/Types/Assistants.types';
import { Checkbox, ListItemIcon, ListItemText } from '@mui/material';
import React, { FC, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

type Props = {
	name: string;
};

// Section order for the picker. Codes are catalogue-owned (backend migration
// seed_grants_catalogue); anything without a group lands in "Other".
const GROUP_ORDER = ['content', 'students', 'submissions', 'codes', 'management'];
const GROUP_LABELS: Record<string, string> = {
	content: 'Content',
	students: 'Students',
	submissions: 'Submissions',
	codes: 'Codes',
	management: 'Management',
	other: 'Other',
};

const Permissions: FC<Props> = ({ name }) => {
	const { control } = useFormContext();
	const {
		field: { onChange, value, onBlur, ref },
	} = useController({
		control,
		name,
	});

	const dispatch = useDispatch();
	const { fetchAll, filters, query } = useTypedSelector(
		(state) => state.Grants
	);
	useEffect(() => {
		// No sortBy: the validator rejects an empty `sortBy: {}` with a 422.
		dispatch(
			grantsGetAll({
				page: 1,
				perPage: 100,
				filters,
				query: query as string,
			} as any)
		);
	}, []);

	const selectedValue: Array<number> = useMemo(() => {
		return [...new Set((value as Array<number> | undefined) ?? [])];
	}, [value]);

	const groups = useMemo(() => {
		const byGroup = new Map<string, Grants[]>();
		(fetchAll ?? []).forEach((grant) => {
			const key = grant.group ?? 'other';
			byGroup.set(key, [...(byGroup.get(key) ?? []), grant]);
		});
		const known = GROUP_ORDER.filter((key) => byGroup.has(key));
		const rest = [...byGroup.keys()].filter((key) => !GROUP_ORDER.includes(key)).sort();
		return [...known, ...rest].map((key) => ({
			key,
			label: GROUP_LABELS[key] ?? key,
			grants: byGroup.get(key) ?? [],
		}));
	}, [fetchAll]);

	const onGrantSelect = (selected: boolean, perValue: number) => {
		if (selected) {
			if (!selectedValue.includes(perValue)) onChange([...selectedValue, perValue]);
			return;
		}
		onChange(selectedValue.filter((_) => _ !== perValue));
	};

	const onGroupSelect = (selected: boolean, grants: Grants[]) => {
		const ids = grants.map((_) => _.id);
		if (selected) {
			onChange([...new Set([...selectedValue, ...ids])]);
			return;
		}
		onChange(selectedValue.filter((_) => !ids.includes(_)));
	};

	return (
		<Wrapper onBlur={onBlur} ref={ref}>
			{groups.map((group) => {
				const selectedCount = group.grants.filter((_) => selectedValue.includes(_.id)).length;
				const allSelected = selectedCount === group.grants.length && group.grants.length > 0;
				return (
					<Section key={`grant-group-${group.key}`}>
						<SectionHeader>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={allSelected}
									indeterminate={selectedCount > 0 && !allSelected}
									onClick={() => onGroupSelect(!allSelected, group.grants)}
									inputProps={{ 'aria-label': `select all ${group.label}` }}
								/>
							</ListItemIcon>
							<ListItemText primary={group.label} />
						</SectionHeader>
						{group.grants.map((grant) => (
							<PermissionContainer
								key={`grant-${grant.id}`}
								permission={grant}
								onGrantSelect={onGrantSelect}
								currentGrant={selectedValue}
							/>
						))}
					</Section>
				);
			})}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding-left: 4.5rem;
`;

const Section = styled.div`
	margin-bottom: 1rem;
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 1.25rem;
	padding: 0.25rem 1rem;
	.MuiTypography-root {
		font-family: Montserrat;
		font-size: 0.9375rem;
		font-weight: 700;
		color: #251b33;
	}
	.MuiListItemIcon-root {
		min-width: 0rem;
	}
	.MuiButtonBase-root {
		padding: 0;
		margin-right: 0.5rem;
	}
	svg {
		width: 1.125rem;
		height: 1.125rem;
	}
`;

export default Permissions;
